import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut,
  Save,
  Plus,
  Trash2,
  ArrowLeft,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import AvailabilityLegend from "@/components/AvailabilityLegend";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api, formatApiError, uploadImage } from "@/lib/api";
import NoIndex from "@/components/NoIndex";
import TechTab from "@/components/admin/TechTab";

export default function AdminDashboard() {
  const { isAuthenticated, checking, logout, authHeaders } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  const [content, setContent] = useState(null);
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [manifesti, setManifesti] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  // dialogs
  const [serviceDialog, setServiceDialog] = useState({ open: false, item: null });
  const [galleryDialog, setGalleryDialog] = useState({ open: false, item: null });
  const [manifestoDialog, setManifestoDialog] = useState({ open: false, item: null });
  const [vehicleDialog, setVehicleDialog] = useState({ open: false, item: null });

  useEffect(() => {
    if (!checking && !isAuthenticated) navigate("/admin", { replace: true });
  }, [isAuthenticated, checking, navigate]);

  const fetchAll = useCallback(async () => {
    try {
      const [c, s, g, m, v, b] = await Promise.all([
        api.get("/content"),
        api.get("/services"),
        api.get("/gallery"),
        api.get("/admin/manifesti", { headers: authHeaders() }),
        api.get("/admin/vehicles", { headers: authHeaders() }),
        api.get("/admin/bookings", { headers: authHeaders() }),
      ]);
      setContent(c.data);
      setServices(s.data);
      setGallery(g.data);
      setManifesti(m.data);
      setVehicles(v.data);
      setBookings(b.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    if (isAuthenticated) fetchAll();
  }, [isAuthenticated, fetchAll]);

  const updateField = (field, value) => setContent((c) => ({ ...c, [field]: value }));

  const saveContent = async () => {
    try {
      await api.put("/content", content, { headers: authHeaders() });
      toast.success(t("admin.saved"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin", { replace: true });
  };

  // --- service CRUD ---
  const saveService = async (data) => {
    try {
      if (data.id) {
        await api.put(`/services/${data.id}`, data, { headers: authHeaders() });
      } else {
        await api.post("/services", data, { headers: authHeaders() });
      }
      toast.success(t("admin.saved"));
      setServiceDialog({ open: false, item: null });
      const s = await api.get("/services");
      setServices(s.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteService = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try {
      await api.delete(`/services/${id}`, { headers: authHeaders() });
      setServices((arr) => arr.filter((x) => x.id !== id));
      toast.success(t("admin.saved"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // --- gallery CRUD ---
  const saveGallery = async (data) => {
    try {
      if (data.id) {
        await api.put(`/gallery/${data.id}`, data, { headers: authHeaders() });
      } else {
        await api.post("/gallery", data, { headers: authHeaders() });
      }
      toast.success(t("admin.saved"));
      setGalleryDialog({ open: false, item: null });
      const g = await api.get("/gallery");
      setGallery(g.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteGallery = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try {
      await api.delete(`/gallery/${id}`, { headers: authHeaders() });
      setGallery((arr) => arr.filter((x) => x.id !== id));
      toast.success(t("admin.saved"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // --- manifesto CRUD ---
  const saveManifesto = async (data) => {
    try {
      if (data.id) {
        await api.put(`/manifesti/${data.id}`, data, { headers: authHeaders() });
      } else {
        await api.post("/manifesti", data, { headers: authHeaders() });
      }
      toast.success(t("admin.saved"));
      setManifestoDialog({ open: false, item: null });
      const r = await api.get("/admin/manifesti", { headers: authHeaders() });
      setManifesti(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteManifesto = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try {
      await api.delete(`/manifesti/${id}`, { headers: authHeaders() });
      setManifesti((arr) => arr.filter((x) => x.id !== id));
      toast.success(t("admin.saved"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleManifestoModule = async (next) => {
    const updated = { ...content, manifesto_enabled: next };
    setContent(updated);
    try {
      await api.put("/content", updated, { headers: authHeaders() });
      toast.success(next ? "Modulo Manifesti attivato" : "Modulo Manifesti disattivato");
    } catch (err) {
      setContent(content); // rollback
      toast.error(formatApiError(err));
    }
  };

  // --- booking module ---
  const toggleBookingModule = async (next) => {
    const updated = { ...content, booking_enabled: next };
    setContent(updated);
    try {
      await api.put("/content", updated, { headers: authHeaders() });
      toast.success(next ? "Modulo Prenotazione attivato" : "Modulo Prenotazione disattivato");
    } catch (err) {
      setContent(content);
      toast.error(formatApiError(err));
    }
  };

  const saveVehicle = async (data) => {
    try {
      if (data.id) {
        await api.put(`/vehicles/${data.id}`, data, { headers: authHeaders() });
      } else {
        await api.post("/vehicles", data, { headers: authHeaders() });
      }
      toast.success(t("admin.saved"));
      setVehicleDialog({ open: false, item: null });
      const r = await api.get("/admin/vehicles", { headers: authHeaders() });
      setVehicles(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteVehicle = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try {
      await api.delete(`/vehicles/${id}`, { headers: authHeaders() });
      setVehicles((arr) => arr.filter((x) => x.id !== id));
      toast.success(t("admin.saved"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateBookingStatus = async (id, status) => {
    try {
      await api.put(`/admin/bookings/${id}`, { status }, { headers: authHeaders() });
      setBookings((arr) => arr.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(t("admin.saved"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try {
      await api.delete(`/admin/bookings/${id}`, { headers: authHeaders() });
      setBookings((arr) => arr.filter((b) => b.id !== id));
      toast.success(t("admin.saved"));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (checking || loading || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-afb-bg">
        <div className="text-afb-muted text-sm tracking-widest uppercase">
          Caricamento...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-afb-bg" data-testid="admin-dashboard">
      <NoIndex enabled={content.seo_hide_admin !== false} />
      <header className="bg-white border-b border-[color:var(--afb-cream)]">
        <div className="afb-container flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-afb-muted hover:text-afb-gold"
              data-testid="dashboard-back-to-site"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {t("admin.back_to_site")}
            </Link>
            <span className="hidden sm:inline serif text-lg text-afb-ink">
              {t("admin.dashboard")}
            </span>
          </div>
          <Button
            onClick={handleLogout}
            variant="outline"
            size="sm"
            data-testid="logout-btn"
            className="border-[color:var(--afb-cream)]"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t("admin.logout")}
          </Button>
        </div>
      </header>

      <main className="afb-container py-10">
        <h1 className="serif text-3xl sm:text-4xl text-afb-ink mb-8">
          Pannello di Amministrazione
        </h1>

        <Tabs defaultValue="general" className="w-full">
          <TabsList className="flex flex-wrap gap-1 bg-white border border-[color:var(--afb-cream)] p-1 h-auto">
            <TabsTrigger value="general" data-testid="tab-general">
              {t("admin.tab_general")}
            </TabsTrigger>
            <TabsTrigger value="about" data-testid="tab-about">
              {t("admin.tab_about")}
            </TabsTrigger>
            <TabsTrigger value="contact" data-testid="tab-contact">
              {t("admin.tab_contact")}
            </TabsTrigger>
            <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">
              {t("admin.tab_whatsapp")}
            </TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services">
              {t("admin.tab_services")}
            </TabsTrigger>
            <TabsTrigger value="gallery" data-testid="tab-gallery">
              {t("admin.tab_gallery")}
            </TabsTrigger>
            <TabsTrigger value="manifesto" data-testid="tab-manifesto">
              {t("admin.tab_manifesto")}
            </TabsTrigger>
            <TabsTrigger value="seo" data-testid="tab-seo">
              {t("admin.tab_seo")}
            </TabsTrigger>
            <TabsTrigger value="booking" data-testid="tab-booking">
              <span className="inline-flex items-center gap-2">
                {t("admin.tab_booking")}
                {bookings.filter((b) => b.status === "new").length > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-semibold leading-none"
                    data-testid="booking-new-badge"
                  >
                    {bookings.filter((b) => b.status === "new").length}
                  </span>
                )}
              </span>
            </TabsTrigger>
            <TabsTrigger value="tech" data-testid="tab-tech">
              {t("admin.tab_tech")}
            </TabsTrigger>
          </TabsList>

          {/* GENERAL */}
          <TabsContent value="general">
            <SectionCard title="Hero">
              <HeroImageEditor
                value={content.hero_image_url}
                overlay={content.hero_overlay ?? 55}
                onUrlChange={(v) => updateField("hero_image_url", v)}
                onOverlayChange={(v) => updateField("hero_overlay", v)}
              />
              <BilingualField
                labelIt="Titolo principale (IT)"
                labelEn="Main title (EN)"
                value={content.hero_title_it}
                valueEn={content.hero_title_en}
                onChangeIt={(v) => updateField("hero_title_it", v)}
                onChangeEn={(v) => updateField("hero_title_en", v)}
                testId="hero-title"
              />
              <BilingualField
                labelIt="Sottotitolo (IT)"
                labelEn="Subtitle (EN)"
                value={content.hero_subtitle_it}
                valueEn={content.hero_subtitle_en}
                onChangeIt={(v) => updateField("hero_subtitle_it", v)}
                onChangeEn={(v) => updateField("hero_subtitle_en", v)}
                testId="hero-subtitle"
              />
              <BilingualField
                labelIt="Tagline (IT)"
                labelEn="Tagline (EN)"
                value={content.hero_tagline_it}
                valueEn={content.hero_tagline_en}
                onChangeIt={(v) => updateField("hero_tagline_it", v)}
                onChangeEn={(v) => updateField("hero_tagline_en", v)}
                testId="hero-tagline"
                multiline
              />
            </SectionCard>
            <SaveBar onSave={saveContent} />
          </TabsContent>

          {/* ABOUT */}
          <TabsContent value="about">
            <SectionCard title="Chi Siamo / Consorzio">
              <BilingualField
                labelIt="Intro citazione (IT)"
                labelEn="Intro quote (EN)"
                value={content.about_intro_it}
                valueEn={content.about_intro_en}
                onChangeIt={(v) => updateField("about_intro_it", v)}
                onChangeEn={(v) => updateField("about_intro_en", v)}
                testId="about-intro"
                multiline
              />
              <BilingualField
                labelIt="Mission (IT)"
                labelEn="Mission (EN)"
                value={content.about_mission_it}
                valueEn={content.about_mission_en}
                onChangeIt={(v) => updateField("about_mission_it", v)}
                onChangeEn={(v) => updateField("about_mission_en", v)}
                testId="about-mission"
                multiline
              />
              <BilingualField
                labelIt="Testo Consorzio (IT)"
                labelEn="Consortium text (EN)"
                value={content.consorzio_text_it}
                valueEn={content.consorzio_text_en}
                onChangeIt={(v) => updateField("consorzio_text_it", v)}
                onChangeEn={(v) => updateField("consorzio_text_en", v)}
                testId="consorzio-text"
                multiline
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  label="Agenzie associate"
                  type="number"
                  value={content.stats_agencies}
                  onChange={(v) => updateField("stats_agencies", parseInt(v || 0, 10))}
                  testId="stats-agencies"
                />
                <Field
                  label="Dipendenti"
                  type="number"
                  value={content.stats_employees}
                  onChange={(v) => updateField("stats_employees", parseInt(v || 0, 10))}
                  testId="stats-employees"
                />
              </div>
            </SectionCard>
            <SaveBar onSave={saveContent} />
          </TabsContent>

          {/* CONTACT */}
          <TabsContent value="contact">
            <SectionCard title="Indirizzi">
              <Field
                label="Sede Melendugno"
                value={content.address_melendugno}
                onChange={(v) => updateField("address_melendugno", v)}
                testId="addr-melendugno"
              />
              <Field
                label="Sede Borgagne"
                value={content.address_borgagne}
                onChange={(v) => updateField("address_borgagne", v)}
                testId="addr-borgagne"
              />
              <Field
                label="Sede Consorzio"
                value={content.address_consorzio}
                onChange={(v) => updateField("address_consorzio", v)}
                testId="addr-consorzio"
              />
            </SectionCard>
            <SectionCard title="Telefoni & Email">
              <Field
                label="Telefono Giuseppe"
                value={content.phone_giuseppe}
                onChange={(v) => updateField("phone_giuseppe", v)}
                testId="phone-giuseppe"
              />
              <Field
                label="Telefono Daniele"
                value={content.phone_daniele}
                onChange={(v) => updateField("phone_daniele", v)}
                testId="phone-daniele"
              />
              <Field
                label="Telefono Fisso"
                value={content.phone_fixed}
                onChange={(v) => updateField("phone_fixed", v)}
                testId="phone-fixed"
              />
              <Field
                label="Email"
                type="email"
                value={content.email}
                onChange={(v) => updateField("email", v)}
                testId="email"
              />
            </SectionCard>
            <SaveBar onSave={saveContent} />
          </TabsContent>

          {/* WHATSAPP */}
          <TabsContent value="whatsapp">
            <SectionCard title="WhatsApp">
              <p className="text-xs text-afb-muted -mt-2">
                Inserire il numero in formato internazionale senza il “+” (es. 393803936347).
              </p>
              <Field
                label="Numero WhatsApp (formato wa.me)"
                value={content.whatsapp_number}
                onChange={(v) => updateField("whatsapp_number", v.replace(/[^0-9]/g, ""))}
                testId="wa-number"
              />
              <Field
                label="Numero visualizzato"
                value={content.whatsapp_display}
                onChange={(v) => updateField("whatsapp_display", v)}
                testId="wa-display"
              />
              <BilingualField
                labelIt="Messaggio precompilato (IT)"
                labelEn="Pre-filled message (EN)"
                value={content.whatsapp_message_it}
                valueEn={content.whatsapp_message_en}
                onChangeIt={(v) => updateField("whatsapp_message_it", v)}
                onChangeEn={(v) => updateField("whatsapp_message_en", v)}
                testId="wa-msg"
                multiline
              />
            </SectionCard>
            <SaveBar onSave={saveContent} />
          </TabsContent>

          {/* SERVICES */}
          <TabsContent value="services">
            <Card className="border-[color:var(--afb-cream)]">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="serif text-xl text-afb-ink">{t("admin.tab_services")}</h2>
                  <Button
                    onClick={() => setServiceDialog({ open: true, item: null })}
                    data-testid="add-service-btn"
                    className="bg-afb-ink hover:bg-black text-white rounded-full"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-1.5" /> {t("admin.add")}
                  </Button>
                </div>
                <div className="space-y-3">
                  {services.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-start justify-between gap-4 p-4 border border-[color:var(--afb-cream)] rounded-md bg-white"
                      data-testid={`admin-service-row-${s.id}`}
                    >
                      <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-widest text-afb-gold">
                          {s.icon} · #{s.order}
                        </div>
                        <div className="serif text-base text-afb-ink mt-1">{s.title_it}</div>
                        <div className="text-xs text-afb-muted">{s.title_en}</div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setServiceDialog({ open: true, item: s })}
                          data-testid={`edit-service-${s.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => deleteService(s.id)}
                          data-testid={`delete-service-${s.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* GALLERY */}
          <TabsContent value="gallery">
            <Card className="border-[color:var(--afb-cream)]">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="serif text-xl text-afb-ink">{t("admin.tab_gallery")}</h2>
                  <Button
                    onClick={() => setGalleryDialog({ open: true, item: null })}
                    data-testid="add-gallery-btn"
                    className="bg-afb-ink hover:bg-black text-white rounded-full"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-1.5" /> {t("admin.add")}
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {gallery.map((g) => (
                    <div
                      key={g.id}
                      className="group relative rounded-md overflow-hidden border border-[color:var(--afb-cream)] bg-white"
                      data-testid={`admin-gallery-row-${g.id}`}
                    >
                      <div className="aspect-[4/3] overflow-hidden">
                        <img src={g.url} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="p-3">
                        <div className="text-xs text-afb-ink truncate">{g.caption_it}</div>
                        <div className="text-[10px] text-afb-muted truncate">
                          {g.caption_en}
                        </div>
                      </div>
                      <div className="absolute top-2 right-2 flex gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="bg-white/90"
                          onClick={() => setGalleryDialog({ open: true, item: g })}
                          data-testid={`edit-gallery-${g.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="bg-white/90"
                          onClick={() => deleteGallery(g.id)}
                          data-testid={`delete-gallery-${g.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* MANIFESTO */}
          <TabsContent value="manifesto">
            <Card className="border-[color:var(--afb-cream)] mt-4">
              <CardContent className="p-6">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-6 pb-5 border-b border-[color:var(--afb-cream)]">
                  <div>
                    <h2 className="serif text-xl text-afb-ink">Manifesto sul sito</h2>
                    <p className="text-sm text-afb-muted mt-1 max-w-md">
                      Quando attivo, il pulsante <strong>Manifesti</strong> appare nel menu del sito e la pagina <code className="text-xs bg-afb-cream px-1 py-0.5 rounded">/manifesto</code> diventa accessibile.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs uppercase tracking-widest text-afb-muted">
                      {content.manifesto_enabled ? "ON" : "OFF"}
                    </span>
                    <Switch
                      checked={!!content.manifesto_enabled}
                      onCheckedChange={toggleManifestoModule}
                      data-testid="manifesto-toggle"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between mb-5">
                  <h3 className="serif text-lg text-afb-ink">Manifesti pubblicati</h3>
                  <Button
                    onClick={() => setManifestoDialog({ open: true, item: null })}
                    data-testid="add-manifesto-btn"
                    className="bg-afb-ink hover:bg-black text-white rounded-full"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-1.5" /> {t("admin.add")}
                  </Button>
                </div>

                {manifesti.length === 0 ? (
                  <div className="text-center py-12 text-sm text-afb-muted border border-dashed border-[color:var(--afb-cream)] rounded-md">
                    Nessun manifesto pubblicato.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {manifesti.map((m) => (
                      <div
                        key={m.id}
                        className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border border-[color:var(--afb-cream)] rounded-md bg-white"
                        data-testid={`admin-manifesto-row-${m.id}`}
                      >
                        <div className="flex items-start gap-4 flex-1 min-w-0">
                          {m.photo_url ? (
                            <img
                              src={m.photo_url}
                              alt=""
                              className="w-12 h-12 object-cover rounded-full border border-[color:var(--afb-cream)]"
                            />
                          ) : (
                            <span className="w-12 h-12 rounded-full bg-afb-cream" />
                          )}
                          <div className="min-w-0">
                            <div className="text-[10px] uppercase tracking-widest text-afb-gold">
                              {m.template_type} · #{m.order}
                            </div>
                            <div className="serif text-base text-afb-ink mt-0.5 truncate">
                              {[m.name_first, m.name_last].filter(Boolean).join(" ") || "—"}
                            </div>
                            <div className="text-xs text-afb-muted truncate">
                              {[m.city, m.date_text].filter(Boolean).join(" · ")}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 self-stretch sm:self-auto">
                          <span
                            className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                              m.status === "active"
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {m.status === "active" ? "Attivo" : "Inattivo"}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => setManifestoDialog({ open: true, item: m })}
                            data-testid={`edit-manifesto-${m.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => deleteManifesto(m.id)}
                            data-testid={`delete-manifesto-${m.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SEO */}
          <TabsContent value="seo">
            <SectionCard title="Titoli e Meta">
              <p className="text-xs text-afb-muted -mt-2">
                Questi campi controllano come Google e i social network mostrano il tuo sito.
                Compila titolo, descrizione e parole chiave per ogni lingua.
              </p>
              <BilingualField
                labelIt="Titolo SEO (IT)"
                labelEn="SEO Title (EN)"
                value={content.seo_title_it}
                valueEn={content.seo_title_en}
                onChangeIt={(v) => updateField("seo_title_it", v)}
                onChangeEn={(v) => updateField("seo_title_en", v)}
                testId="seo-title"
              />
              <BilingualField
                labelIt="Meta description (IT) — max ~160 caratteri"
                labelEn="Meta description (EN) — max ~160 chars"
                value={content.seo_description_it}
                valueEn={content.seo_description_en}
                onChangeIt={(v) => updateField("seo_description_it", v)}
                onChangeEn={(v) => updateField("seo_description_en", v)}
                testId="seo-description"
                multiline
              />
              <BilingualField
                labelIt="Parole chiave (IT, separate da virgola)"
                labelEn="Keywords (EN, comma separated)"
                value={content.seo_keywords_it}
                valueEn={content.seo_keywords_en}
                onChangeIt={(v) => updateField("seo_keywords_it", v)}
                onChangeEn={(v) => updateField("seo_keywords_en", v)}
                testId="seo-keywords"
                multiline
              />
            </SectionCard>

            <SectionCard title="Robot e Indicizzazione">
              <label
                className="flex items-start gap-3 p-3 border border-[color:var(--afb-cream)] rounded-md cursor-pointer hover:bg-afb-cream/40"
                data-testid="seo-hide-admin-row"
              >
                <input
                  type="checkbox"
                  checked={content.seo_hide_admin !== false}
                  onChange={(e) => updateField("seo_hide_admin", e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[color:var(--afb-gold)]"
                  data-testid="seo-hide-admin"
                />
                <span>
                  <span className="text-sm text-afb-ink font-medium">
                    Nascondi la rotta /admin a Google
                  </span>
                  <span className="block text-xs text-afb-muted mt-0.5">
                    Aggiunge <code className="text-[10px] bg-afb-cream px-1 rounded">noindex, nofollow</code> alle pagine /admin e /admin/dashboard. Inoltre la rotta è già esclusa via robots.txt. Lascia attivo (consigliato).
                  </span>
                </span>
              </label>
              <div>
                <Label className="text-xs uppercase tracking-widest text-afb-muted">
                  Direttive robots
                </Label>
                <select
                  value={content.seo_robots || "index, follow"}
                  onChange={(e) => updateField("seo_robots", e.target.value)}
                  className="mt-2 w-full h-10 px-3 rounded-md border border-[color:var(--afb-cream)] bg-white text-sm"
                  data-testid="seo-robots"
                >
                  <option value="index, follow">
                    index, follow — il sito appare su Google (consigliato)
                  </option>
                  <option value="noindex, follow">
                    noindex, follow — non indicizzare ma segui i link
                  </option>
                  <option value="index, nofollow">
                    index, nofollow — indicizza ma non seguire i link
                  </option>
                  <option value="noindex, nofollow">
                    noindex, nofollow — sito invisibile ai motori
                  </option>
                </select>
                <p className="text-xs text-afb-muted mt-1">
                  Lascia su <code className="text-[10px] bg-afb-cream px-1 rounded">index, follow</code> per essere trovato su Google.
                </p>
              </div>

              <Field
                label="Codice di verifica Google Search Console"
                value={content.seo_google_verification}
                onChange={(v) => updateField("seo_google_verification", v)}
                testId="seo-google-verification"
              />
              <p className="text-xs text-afb-muted -mt-3">
                Ottienilo da{" "}
                <a
                  href="https://search.google.com/search-console"
                  target="_blank"
                  rel="noreferrer"
                  className="text-afb-gold underline"
                >
                  Google Search Console
                </a>{" "}
                → metodo "tag HTML" → copia solo il valore <code>content="..."</code>.
              </p>

              <Field
                label="URL canonico (lascia vuoto = URL automatico)"
                value={content.seo_canonical_url}
                onChange={(v) => updateField("seo_canonical_url", v)}
                testId="seo-canonical"
              />

              <Field
                label="Autore"
                value={content.seo_author}
                onChange={(v) => updateField("seo_author", v)}
                testId="seo-author"
              />
            </SectionCard>

            <SectionCard title="Open Graph / Anteprima social">
              <p className="text-xs text-afb-muted -mt-2">
                Quando qualcuno condivide il sito su WhatsApp, Facebook o LinkedIn, mostrerà questa immagine.
              </p>
              <SeoOgImageEditor
                value={content.seo_og_image}
                onChange={(v) => updateField("seo_og_image", v)}
              />
            </SectionCard>

            <SectionCard title="File tecnici">
              <div className="space-y-2 text-sm text-afb-ink/85">
                <div className="flex items-start gap-3">
                  <span className="text-afb-gold mt-1">•</span>
                  <div>
                    <a
                      href="/robots.txt"
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-afb-ink underline hover:text-afb-gold"
                    >
                      /robots.txt
                    </a>{" "}
                    — disponibile e accessibile ai crawler.
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-afb-gold mt-1">•</span>
                  <div>
                    <a
                      href="/sitemap.xml"
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-afb-ink underline hover:text-afb-gold"
                    >
                      /sitemap.xml
                    </a>{" "}
                    — invialo a Google Search Console per accelerare l'indicizzazione.
                  </div>
                </div>
              </div>
            </SectionCard>

            <SaveBar onSave={saveContent} />
          </TabsContent>

          {/* BOOKING */}
          <TabsContent value="booking">
            <Card className="border-[color:var(--afb-cream)] mt-4">
              <CardContent className="p-6">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-6 pb-5 border-b border-[color:var(--afb-cream)]">
                  <div>
                    <h2 className="serif text-xl text-afb-ink">Prenotazione sul sito</h2>
                    <p className="text-sm text-afb-muted mt-1 max-w-md">
                      Quando attiva, il pulsante <strong>{content.booking_label_it || "Prenotazione"}</strong> appare nel menu e la pagina <code className="text-xs bg-afb-cream px-1 py-0.5 rounded">/prenotazione</code> diventa accessibile.
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs uppercase tracking-widest text-afb-muted">
                      {content.booking_enabled ? "ON" : "OFF"}
                    </span>
                    <Switch
                      checked={!!content.booking_enabled}
                      onCheckedChange={toggleBookingModule}
                      data-testid="booking-toggle"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
                  <Field
                    label="Etichetta menu (IT)"
                    value={content.booking_label_it}
                    onChange={(v) => updateField("booking_label_it", v)}
                    testId="booking-label-it"
                  />
                  <Field
                    label="Etichetta menu (EN)"
                    value={content.booking_label_en}
                    onChange={(v) => updateField("booking_label_en", v)}
                    testId="booking-label-en"
                  />
                  <Field
                    label="Email destinataria richieste"
                    type="email"
                    value={content.booking_email}
                    onChange={(v) => updateField("booking_email", v)}
                    testId="booking-email"
                  />
                </div>
                <BilingualField
                  labelIt="Intro pagina prenotazione (IT)"
                  labelEn="Intro booking page (EN)"
                  value={content.booking_intro_it}
                  valueEn={content.booking_intro_en}
                  onChangeIt={(v) => updateField("booking_intro_it", v)}
                  onChangeEn={(v) => updateField("booking_intro_en", v)}
                  testId="booking-intro"
                  multiline
                />

                <div className="mt-6 pt-5 border-t border-[color:var(--afb-cream)]">
                  <ServiceTypesEditor
                    value={content.service_types}
                    onChange={(v) => updateField("service_types", v)}
                  />
                </div>

                <div className="mt-6 pt-5 border-t border-[color:var(--afb-cream)]">
                  <h3 className="serif text-base text-afb-ink mb-3">Configurazione Email</h3>
                  <p className="text-xs text-afb-muted mb-4">
                    Se configuri un server SMTP qui sotto, verrà usato in priorità (es. il tuo server <code>libero.it</code>). Altrimenti il sistema usa Resend come fallback.
                  </p>

                  <div className="p-4 bg-afb-cream/40 rounded-md mb-5 border border-[color:var(--afb-cream)]">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-medium text-afb-ink">SMTP — Il tuo server di posta</h4>
                      <span
                        className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                          content.smtp_host ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {content.smtp_host ? "Attivo" : "Non configurato"}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Field
                        label="Server SMTP (host)"
                        value={content.smtp_host}
                        onChange={(v) => updateField("smtp_host", v)}
                        testId="smtp-host"
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <Field
                          label="Porta"
                          type="number"
                          value={content.smtp_port}
                          onChange={(v) => updateField("smtp_port", parseInt(v || 587, 10))}
                          testId="smtp-port"
                        />
                        <div>
                          <Label className="text-xs uppercase tracking-widest text-afb-muted">
                            Sicurezza
                          </Label>
                          <select
                            value={content.smtp_secure || "starttls"}
                            onChange={(e) => updateField("smtp_secure", e.target.value)}
                            className="mt-2 w-full h-10 px-3 rounded-md border border-[color:var(--afb-cream)] bg-white text-sm"
                            data-testid="smtp-secure"
                          >
                            <option value="starttls">STARTTLS (porta 587)</option>
                            <option value="ssl">SSL/TLS (porta 465)</option>
                            <option value="none">Nessuna (sconsigliato)</option>
                          </select>
                        </div>
                      </div>
                      <Field
                        label="Utente / Email"
                        value={content.smtp_user}
                        onChange={(v) => updateField("smtp_user", v)}
                        testId="smtp-user"
                      />
                      <Field
                        label="Password"
                        type="password"
                        value={content.smtp_password}
                        onChange={(v) => updateField("smtp_password", v)}
                        testId="smtp-password"
                      />
                      <Field
                        label="From (mittente, lascia vuoto = stesso utente)"
                        value={content.smtp_from}
                        onChange={(v) => updateField("smtp_from", v)}
                        testId="smtp-from"
                      />
                    </div>
                    <div className="mt-3 text-xs text-afb-muted leading-relaxed">
                      <strong>Esempio Libero/Aruba:</strong> host{" "}
                      <code className="bg-white px-1 rounded">smtphz.aruba.it</code> · porta{" "}
                      <code className="bg-white px-1 rounded">465</code> · SSL/TLS · utente = email completa · password = quella della casella.
                      <br />
                      <strong>Libero classico:</strong> host{" "}
                      <code className="bg-white px-1 rounded">smtp.libero.it</code> · porta{" "}
                      <code className="bg-white px-1 rounded">465</code> · SSL/TLS.
                    </div>
                  </div>

                  <details className="text-sm">
                    <summary className="cursor-pointer text-afb-muted">
                      Mostra opzioni Resend (fallback)
                    </summary>
                    <div className="mt-3 p-4 bg-afb-cream/40 rounded-md border border-[color:var(--afb-cream)]">
                      <p className="text-xs text-afb-muted mb-3">
                        Usata solo se SMTP non è configurato.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field
                          label="Resend API Key (re_...)"
                          type="password"
                          value={content.resend_api_key}
                          onChange={(v) => updateField("resend_api_key", v)}
                          testId="resend-api-key"
                        />
                        <Field
                          label="From email"
                          type="email"
                          value={content.resend_from_email}
                          onChange={(v) => updateField("resend_from_email", v)}
                          testId="resend-from-email"
                        />
                      </div>
                    </div>
                  </details>
                </div>

                <SaveBar onSave={saveContent} />
              </CardContent>
            </Card>

            {/* VEHICLES */}
            <Card className="border-[color:var(--afb-cream)] mt-6">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="serif text-xl text-afb-ink">Veicoli</h2>
                  <Button
                    onClick={() => setVehicleDialog({ open: true, item: null })}
                    data-testid="add-vehicle-btn"
                    className="bg-afb-ink hover:bg-black text-white rounded-full"
                    size="sm"
                  >
                    <Plus className="w-4 h-4 mr-1.5" /> {t("admin.add")}
                  </Button>
                </div>
                {vehicles.length === 0 ? (
                  <div className="text-center py-10 text-sm text-afb-muted border border-dashed border-[color:var(--afb-cream)] rounded-md">
                    Nessun veicolo. Aggiungi un veicolo (es. Maserati 1, Mercedes...).
                  </div>
                ) : (
                  <div className="space-y-3">
                    {vehicles.map((v) => (
                      <div
                        key={v.id}
                        className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border border-[color:var(--afb-cream)] rounded-md bg-white"
                        data-testid={`admin-vehicle-row-${v.id}`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {v.photo_url ? (
                            <img src={v.photo_url} alt="" className="w-14 h-14 object-cover rounded border border-[color:var(--afb-cream)]" />
                          ) : (
                            <span className="w-14 h-14 rounded bg-afb-cream" />
                          )}
                          <div className="min-w-0">
                            <div className="serif text-base text-afb-ink truncate">{v.name}</div>
                            <div className="text-xs text-afb-muted truncate">
                              {v.model_label}
                              {v.targa && ` · ${v.targa}`}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-afb-muted mt-0.5">
                              {v.blocked_dates.length} giorni bloccati
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${v.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                            {v.active ? "Attivo" : "Disattivo"}
                          </span>
                          <Button variant="outline" size="icon" onClick={() => setVehicleDialog({ open: true, item: v })} data-testid={`edit-vehicle-${v.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="outline" size="icon" onClick={() => deleteVehicle(v.id)} data-testid={`delete-vehicle-${v.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* BOOKINGS LIST */}
            <Card className="border-[color:var(--afb-cream)] mt-6">
              <CardContent className="p-6">
                <h2 className="serif text-xl text-afb-ink mb-5">Richieste ricevute</h2>
                {bookings.length === 0 ? (
                  <div className="text-center py-10 text-sm text-afb-muted border border-dashed border-[color:var(--afb-cream)] rounded-md">
                    Nessuna richiesta al momento.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {bookings.map((b) => (
                      <div
                        key={b.id}
                        className="p-4 border border-[color:var(--afb-cream)] rounded-md bg-white"
                        data-testid={`admin-booking-row-${b.id}`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="serif text-base text-afb-ink">
                              {b.customer_name}
                              {b.customer_company && (
                                <span className="text-afb-muted text-sm font-normal">
                                  {" "}· {b.customer_company}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-afb-muted mt-0.5">
                              {b.vehicle_name} · {b.dates.join(", ")}
                            </div>
                            <div className="text-xs text-afb-muted mt-0.5">
                              {b.customer_email} {b.customer_phone && `· ${b.customer_phone}`}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-afb-gold mt-1">
                              {(() => {
                                const types = content?.service_types || [];
                                const match = types.find((s) => s.value === b.service_type);
                                if (match) return match.label_it || match.value;
                                return {
                                  without_driver: "Senza autista",
                                  with_driver: "Con autista",
                                  with_driver_helper: "Con autista + necroforo",
                                }[b.service_type] || b.service_type;
                              })()}
                            </div>
                            {b.notes && (
                              <p className="text-sm text-afb-ink/85 mt-2 italic">"{b.notes}"</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <select
                              value={b.status}
                              onChange={(e) => updateBookingStatus(b.id, e.target.value)}
                              className="text-xs h-8 px-2 rounded border border-[color:var(--afb-cream)] bg-white"
                              data-testid={`booking-status-${b.id}`}
                            >
                              <option value="new">Nuova</option>
                              <option value="confirmed">Confermata</option>
                              <option value="cancelled">Annullata</option>
                            </select>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => deleteBooking(b.id)}
                              data-testid={`delete-booking-${b.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TECH / IT */}
          <TabsContent value="tech">
            <TechTab />
          </TabsContent>
        </Tabs>
      </main>

      <ServiceDialog
        state={serviceDialog}
        onClose={() => setServiceDialog({ open: false, item: null })}
        onSave={saveService}
      />
      <GalleryDialog
        state={galleryDialog}
        onClose={() => setGalleryDialog({ open: false, item: null })}
        onSave={saveGallery}
      />
      <ManifestoDialog
        state={manifestoDialog}
        onClose={() => setManifestoDialog({ open: false, item: null })}
        onSave={saveManifesto}
      />
      <VehicleDialog
        state={vehicleDialog}
        onClose={() => setVehicleDialog({ open: false, item: null })}
        onSave={saveVehicle}
      />
    </div>
  );
}

/* ---- Helper components ---- */

function HeroImageEditor({ value, overlay, onUrlChange, onOverlayChange }) {
  const { authHeaders } = useAuth();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 6MB)");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadImage(file, authHeaders());
      onUrlChange(url);
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const previewSrc =
    value ||
    "https://customer-assets.emergentagent.com/job_85512f74-aa35-4a50-bb9f-c899943066c1/artifacts/now99qsr_468303231_122171145896137402_7412612925861017942_n.jpg";

  const ov = Math.max(0, Math.min(100, overlay ?? 55)) / 100;
  const top = Math.min(1, ov + 0.1);
  const mid = Math.max(0, ov - 0.15);
  const bottom = Math.min(1, ov + 0.15);

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs uppercase tracking-widest text-afb-muted">
          Immagine Hero
        </Label>
        <p className="text-xs text-afb-muted mt-1">
          Lascia vuoto per usare l'immagine predefinita. Se carichi/incolli un'altra
          immagine e poi la rimuovi, il sito tornerà automaticamente alla foto di default.
        </p>
      </div>

      <div className="relative rounded-md overflow-hidden border border-[color:var(--afb-cream)] aspect-[16/7] bg-black">
        <img src={previewSrc} alt="hero preview" className="absolute inset-0 w-full h-full object-cover" />
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to bottom, rgba(0,0,0,${top.toFixed(
              2
            )}), rgba(0,0,0,${mid.toFixed(2)}), rgba(0,0,0,${bottom.toFixed(2)}))`,
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="serif text-white text-2xl sm:text-3xl drop-shadow">
            Anteprima Hero
          </span>
        </div>
        {!value && (
          <span className="absolute top-2 left-2 text-[10px] uppercase tracking-widest bg-white/90 text-afb-ink px-2 py-0.5 rounded-full">
            Default
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-end">
        <Field
          label="URL immagine (opzionale)"
          value={value || ""}
          onChange={onUrlChange}
          testId="hero-image-url"
        />
        <div>
          <input
            id="hero-upload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFile}
            data-testid="hero-image-file-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => document.getElementById("hero-upload")?.click()}
            disabled={uploading}
            data-testid="hero-image-upload-btn"
          >
            {uploading ? "Caricamento..." : "Carica file"}
          </Button>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => onUrlChange("")}
          disabled={!value}
          data-testid="hero-image-reset-btn"
        >
          Ripristina default
        </Button>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-widest text-afb-muted">
            Visibilità immagine
          </Label>
          <span className="text-xs text-afb-muted tabular-nums">
            Sfumatura: {overlay ?? 55}%
          </span>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest text-afb-muted whitespace-nowrap">
            Più nitida
          </span>
          <Slider
            value={[overlay ?? 55]}
            min={0}
            max={100}
            step={1}
            onValueChange={(v) => onOverlayChange(v[0])}
            className="flex-1"
            data-testid="hero-overlay-slider"
          />
          <span className="text-[10px] uppercase tracking-widest text-afb-muted whitespace-nowrap">
            Più scura
          </span>
        </div>
      </div>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <Card className="border-[color:var(--afb-cream)] mt-4">
      <CardContent className="p-6 space-y-5">
        <h2 className="serif text-xl text-afb-ink">{title}</h2>
        {children}
      </CardContent>
    </Card>
  );
}

function SeoOgImageEditor({ value, onChange }) {
  const { authHeaders } = useAuth();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 6MB)");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadImage(file, authHeaders());
      onChange(url);
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-3">
      {value ? (
        <div className="rounded-md overflow-hidden border border-[color:var(--afb-cream)] bg-black aspect-[1.91/1] max-w-md">
          <img src={value} alt="og preview" className="w-full h-full object-cover" />
        </div>
      ) : (
        <p className="text-xs text-afb-muted">
          Nessuna immagine caricata — verrà usata l'immagine Hero.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <input
          id="seo-og-upload"
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
          data-testid="seo-og-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={() => document.getElementById("seo-og-upload")?.click()}
          disabled={uploading}
          data-testid="seo-og-upload-btn"
        >
          {uploading ? "Caricamento..." : value ? "Sostituisci" : "Carica immagine"}
        </Button>
        {value && (
          <Button
            type="button"
            variant="outline"
            onClick={() => onChange("")}
            data-testid="seo-og-clear"
          >
            Rimuovi
          </Button>
        )}
      </div>
      <p className="text-xs text-afb-muted">
        Dimensione consigliata: <strong>1200 × 630 px</strong>.
      </p>
    </div>
  );
}

function SaveBar({ onSave }) {
  return (
    <div className="flex justify-end pt-5">
      <Button
        onClick={onSave}
        className="bg-afb-ink hover:bg-black text-white rounded-full"
        data-testid="save-content-btn"
      >
        <Save className="w-4 h-4 mr-2" />
        Salva modifiche
      </Button>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, multiline = false }) {
  const Comp = multiline ? Textarea : Input;
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-afb-muted">{label}</Label>
      <Comp
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        rows={multiline ? 4 : undefined}
        className="mt-2"
        data-testid={testId}
      />
    </div>
  );
}

function BilingualField({
  labelIt,
  labelEn,
  value,
  valueEn,
  onChangeIt,
  onChangeEn,
  testId,
  multiline,
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field
        label={labelIt}
        value={value}
        onChange={onChangeIt}
        testId={`${testId}-it`}
        multiline={multiline}
      />
      <Field
        label={labelEn}
        value={valueEn}
        onChange={onChangeEn}
        testId={`${testId}-en`}
        multiline={multiline}
      />
    </div>
  );
}

function ServiceDialog({ state, onClose, onSave }) {
  const empty = {
    id: null,
    title_it: "",
    title_en: "",
    description_it: "",
    description_en: "",
    icon: "Sparkles",
    order: 0,
  };
  const [form, setForm] = useState(empty);

  useEffect(() => {
    if (state.open) setForm(state.item ? { ...state.item } : empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.open, state.item]);

  const submit = (e) => {
    e.preventDefault();
    const payload = {
      title_it: form.title_it,
      title_en: form.title_en,
      description_it: form.description_it,
      description_en: form.description_en,
      icon: form.icon,
      order: parseInt(form.order || 0, 10),
    };
    if (form.id) payload.id = form.id;
    onSave(payload);
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="service-dialog">
        <DialogHeader>
          <DialogTitle>{form.id ? "Modifica servizio" : "Nuovo servizio"}</DialogTitle>
          <DialogDescription>
            Inserisci titolo e descrizione in italiano e inglese, scegli un'icona di lucide-react e l'ordine di visualizzazione.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Titolo IT"
              value={form.title_it}
              onChange={(v) => setForm((f) => ({ ...f, title_it: v }))}
              testId="svc-title-it"
            />
            <Field
              label="Titolo EN"
              value={form.title_en}
              onChange={(v) => setForm((f) => ({ ...f, title_en: v }))}
              testId="svc-title-en"
            />
          </div>
          <Field
            label="Descrizione IT"
            value={form.description_it}
            onChange={(v) => setForm((f) => ({ ...f, description_it: v }))}
            testId="svc-desc-it"
            multiline
          />
          <Field
            label="Descrizione EN"
            value={form.description_en}
            onChange={(v) => setForm((f) => ({ ...f, description_en: v }))}
            testId="svc-desc-en"
            multiline
          />
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Icona (lucide-react)"
              value={form.icon}
              onChange={(v) => setForm((f) => ({ ...f, icon: v }))}
              testId="svc-icon"
            />
            <Field
              label="Ordine"
              type="number"
              value={form.order}
              onChange={(v) => setForm((f) => ({ ...f, order: v }))}
              testId="svc-order"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Annulla
            </Button>
            <Button
              type="submit"
              className="bg-afb-ink hover:bg-black text-white"
              data-testid="svc-save"
            >
              Salva
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function GalleryDialog({ state, onClose, onSave }) {
  const { authHeaders } = useAuth();
  const empty = { id: null, url: "", caption_it: "", caption_en: "", order: 0 };
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (state.open) setForm(state.item ? { ...state.item } : empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.open, state.item]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.url) {
      toast.error("Carica un'immagine prima di salvare");
      return;
    }
    const payload = {
      url: form.url,
      caption_it: form.caption_it,
      caption_en: form.caption_en,
      order: parseInt(form.order || 0, 10),
    };
    if (form.id) payload.id = form.id;
    onSave(payload);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 6MB)");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadImage(file, authHeaders());
      setForm((f) => ({ ...f, url }));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="gallery-dialog">
        <DialogHeader>
          <DialogTitle>{form.id ? "Modifica foto" : "Nuova foto"}</DialogTitle>
          <DialogDescription>
            Carica una foto dal tuo dispositivo e aggiungi le didascalie in italiano e inglese.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-afb-muted">
              Immagine
            </Label>
            <input
              id="gallery-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFile}
              data-testid="gal-file-input"
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById("gallery-upload")?.click()}
                disabled={uploading}
                data-testid="gal-upload-btn"
              >
                {uploading ? "Caricamento..." : form.url ? "Sostituisci file" : "Carica file"}
              </Button>
              {form.url && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setForm((f) => ({ ...f, url: "" }))}
                  data-testid="gal-clear-btn"
                >
                  Rimuovi
                </Button>
              )}
            </div>
          </div>
          {form.url && (
            <div className="aspect-[4/3] overflow-hidden rounded-md border border-[color:var(--afb-cream)] bg-black">
              <img src={form.url} alt="preview" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field
              label="Didascalia IT"
              value={form.caption_it}
              onChange={(v) => setForm((f) => ({ ...f, caption_it: v }))}
              testId="gal-caption-it"
            />
            <Field
              label="Didascalia EN"
              value={form.caption_en}
              onChange={(v) => setForm((f) => ({ ...f, caption_en: v }))}
              testId="gal-caption-en"
            />
          </div>
          <Field
            label="Ordine"
            type="number"
            value={form.order}
            onChange={(v) => setForm((f) => ({ ...f, order: v }))}
            testId="gal-order"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Annulla
            </Button>
            <Button
              type="submit"
              className="bg-afb-ink hover:bg-black text-white"
              data-testid="gal-save"
            >
              Salva
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function ManifestoDialog({ state, onClose, onSave }) {
  const { authHeaders } = useAuth();
  const empty = {
    id: null,
    template_type: "annuncio",
    photo_url: "",
    decoration_url: "",
    show_badge: false,
    name_first: "",
    name_last: "",
    age: "",
    relation: "",
    intro_text: "E' venuto/a a mancare all'affetto dei suoi cari all'età di",
    announce_text:
      "Ne danno il triste annuncio: i Figli, le Nuore, la Sorella, i Cognati, le Cognate, i Nipoti e i parenti tutti.",
    city: "",
    date_text: "",
    funeral_text: "",
    extra_text: "",
    facebook_text:
      "Per chi volesse può lasciare un messaggio di cordoglio sulla pagina Facebook Funeral Service",
    footer_note: "Il presente vale come ringraziamento",
    status: "active",
    order: 0,
  };
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(null); // 'photo' | 'decoration' | null

  useEffect(() => {
    if (state.open) setForm(state.item ? { ...empty, ...state.item } : empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.open, state.item]);

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name_first && !form.name_last) {
      toast.error("Inserisci almeno il nome");
      return;
    }
    const payload = { ...form, order: parseInt(form.order || 0, 10) };
    delete payload.id;
    if (form.id) payload.id = form.id;
    onSave(payload);
  };

  const handleFile = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 6MB)");
      return;
    }
    setUploading(kind);
    try {
      const url = await uploadImage(file, authHeaders());
      setForm((f) => ({ ...f, [kind === "photo" ? "photo_url" : "decoration_url"]: url }));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(null);
      e.target.value = "";
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="manifesto-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {form.id ? "Modifica manifesto" : "Nuovo manifesto"}
          </DialogTitle>
          <DialogDescription>
            Compila i campi del manifesto. Tutti i campi sono opzionali eccetto il nome.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-afb-muted">
                Tipo
              </Label>
              <select
                value={form.template_type}
                onChange={(e) => upd("template_type")(e.target.value)}
                className="mt-2 w-full h-10 px-3 rounded-md border border-[color:var(--afb-cream)] bg-white text-sm"
                data-testid="man-type"
              >
                <option value="annuncio">Annuncio</option>
                <option value="ringraziamento">Ringraziamento</option>
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-afb-muted">
                Stato
              </Label>
              <select
                value={form.status}
                onChange={(e) => upd("status")(e.target.value)}
                className="mt-2 w-full h-10 px-3 rounded-md border border-[color:var(--afb-cream)] bg-white text-sm"
                data-testid="man-status"
              >
                <option value="active">Attivo (visibile sul sito)</option>
                <option value="inactive">Inattivo (nascosto)</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-afb-muted">
              Foto della persona (cerchio)
            </Label>
            <input
              id="manifesto-photo-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e, "photo")}
              data-testid="man-photo-input"
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById("manifesto-photo-upload")?.click()}
                disabled={uploading === "photo"}
                data-testid="man-photo-upload-btn"
              >
                {uploading === "photo" ? "Caricamento..." : form.photo_url ? "Sostituisci" : "Carica foto"}
              </Button>
              {form.photo_url && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => upd("photo_url")("")}
                  data-testid="man-photo-clear"
                >
                  Rimuovi
                </Button>
              )}
              {form.photo_url && (
                <img
                  src={form.photo_url}
                  alt="preview"
                  className="w-14 h-14 object-cover rounded-full border border-[color:var(--afb-cream)]"
                />
              )}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-afb-muted">
              Decorazione laterale (Madonna, croce, rose...)
            </Label>
            <p className="text-xs text-afb-muted mt-1">
              Si visualizza nella colonna sinistra del manifesto. Lascia vuoto per non mostrare alcuna decorazione.
            </p>
            <input
              id="manifesto-decoration-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleFile(e, "decoration")}
              data-testid="man-decoration-input"
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById("manifesto-decoration-upload")?.click()}
                disabled={uploading === "decoration"}
                data-testid="man-decoration-upload-btn"
              >
                {uploading === "decoration"
                  ? "Caricamento..."
                  : form.decoration_url
                    ? "Sostituisci decorazione"
                    : "Carica decorazione"}
              </Button>
              {form.decoration_url && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => upd("decoration_url")("")}
                  data-testid="man-decoration-clear"
                >
                  Rimuovi
                </Button>
              )}
              {form.decoration_url && (
                <img
                  src={form.decoration_url}
                  alt="decoration"
                  className="w-14 h-14 object-contain border border-[color:var(--afb-cream)] bg-white p-1 rounded"
                />
              )}
            </div>
          </div>

          <label
            className="flex items-start gap-3 p-3 border border-[color:var(--afb-cream)] rounded-md cursor-pointer hover:bg-afb-cream/40"
            data-testid="man-show-badge-row"
          >
            <input
              type="checkbox"
              checked={!!form.show_badge}
              onChange={(e) => upd("show_badge")(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-[color:var(--afb-gold)]"
              data-testid="man-show-badge"
            />
            <span>
              <span className="text-sm text-afb-ink font-medium">
                Mostra il badge “Funeral Service” nell'angolo in basso a destra
              </span>
              <span className="block text-xs text-afb-muted mt-0.5">
                Logo circolare Onoranze Funebri Barone — Melendugno · Borgagne.
              </span>
            </span>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field
              label="Nome"
              value={form.name_first}
              onChange={upd("name_first")}
              testId="man-name-first"
            />
            <Field
              label="Cognome"
              value={form.name_last}
              onChange={upd("name_last")}
              testId="man-name-last"
            />
            <Field
              label="Età"
              value={form.age}
              onChange={upd("age")}
              testId="man-age"
            />
          </div>

          <Field
            label="Relazione (es. vedova Ciccarese)"
            value={form.relation}
            onChange={upd("relation")}
            testId="man-relation"
          />

          <Field
            label="Frase introduttiva"
            value={form.intro_text}
            onChange={upd("intro_text")}
            testId="man-intro"
            multiline
          />

          <Field
            label="Testo annuncio (chi annuncia)"
            value={form.announce_text}
            onChange={upd("announce_text")}
            testId="man-announce"
            multiline
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label="Città"
              value={form.city}
              onChange={upd("city")}
              testId="man-city"
            />
            <Field
              label="Data (testo, es. 31 Marzo 2026)"
              value={form.date_text}
              onChange={upd("date_text")}
              testId="man-date"
            />
          </div>

          <Field
            label="Dettagli funerale"
            value={form.funeral_text}
            onChange={upd("funeral_text")}
            testId="man-funeral"
            multiline
          />

          <Field
            label="Testo aggiuntivo (opzionale)"
            value={form.extra_text}
            onChange={upd("extra_text")}
            testId="man-extra"
            multiline
          />

          <Field
            label="Messaggio Facebook"
            value={form.facebook_text}
            onChange={upd("facebook_text")}
            testId="man-facebook"
            multiline
          />

          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
            <Field
              label="Nota a piè di pagina"
              value={form.footer_note}
              onChange={upd("footer_note")}
              testId="man-footer"
            />
            <Field
              label="Ordine"
              type="number"
              value={form.order}
              onChange={upd("order")}
              testId="man-order"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Annulla
            </Button>
            <Button
              type="submit"
              className="bg-afb-ink hover:bg-black text-white"
              data-testid="man-save"
            >
              Salva
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function VehicleDialog({ state, onClose, onSave }) {
  const { authHeaders } = useAuth();
  const empty = {
    id: null,
    name: "",
    targa: "",
    model_label: "",
    photo_url: "",
    active: true,
    blocked_dates: [],
    order: 0,
  };
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (state.open) setForm(state.item ? { ...empty, ...state.item } : empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.open, state.item]);

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error("Immagine troppo grande (max 6MB)");
      return;
    }
    setUploading(true);
    try {
      const url = await uploadImage(file, authHeaders());
      setForm((f) => ({ ...f, photo_url: url }));
      toast.success("Immagine caricata");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  // Blocked dates as Date objects
  const blockedAsDates = form.blocked_dates.map((s) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  });
  const toISOLocal = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const onCalendarSelect = (dates) => {
    const sorted = (dates || []).map(toISOLocal).sort();
    setForm((f) => ({ ...f, blocked_dates: sorted }));
  };

  const blockWholeMonth = (year, monthIdx) => {
    const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
    const additions = [];
    for (let d = 1; d <= daysInMonth; d++) {
      additions.push(
        `${year}-${String(monthIdx + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`
      );
    }
    setForm((f) => ({
      ...f,
      blocked_dates: Array.from(new Set([...f.blocked_dates, ...additions])).sort(),
    }));
  };

  const clearMonth = (year, monthIdx) => {
    const prefix = `${year}-${String(monthIdx + 1).padStart(2, "0")}`;
    setForm((f) => ({
      ...f,
      blocked_dates: f.blocked_dates.filter((d) => !d.startsWith(prefix)),
    }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Inserisci il nome del veicolo");
      return;
    }
    const payload = {
      name: form.name,
      targa: form.targa,
      model_label: form.model_label,
      photo_url: form.photo_url,
      active: !!form.active,
      blocked_dates: form.blocked_dates,
      order: parseInt(form.order || 0, 10),
    };
    if (form.id) payload.id = form.id;
    onSave(payload);
  };

  const today = new Date();

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="vehicle-dialog"
      >
        <DialogHeader>
          <DialogTitle>{form.id ? "Modifica veicolo" : "Nuovo veicolo"}</DialogTitle>
          <DialogDescription>
            Inserisci nome, targa e seleziona le date in cui il veicolo NON è disponibile.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label="Nome (es. Maserati 1)"
              value={form.name}
              onChange={upd("name")}
              testId="veh-name"
            />
            <Field
              label="Targa"
              value={form.targa}
              onChange={upd("targa")}
              testId="veh-targa"
            />
          </div>
          <Field
            label="Modello / descrizione"
            value={form.model_label}
            onChange={upd("model_label")}
            testId="veh-model"
          />

          <div>
            <Label className="text-xs uppercase tracking-widest text-afb-muted">
              Foto veicolo
            </Label>
            <input
              id="vehicle-photo-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFile}
              data-testid="veh-photo-input"
            />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => document.getElementById("vehicle-photo-upload")?.click()}
                disabled={uploading}
                data-testid="veh-photo-upload"
              >
                {uploading ? "Caricamento..." : form.photo_url ? "Sostituisci" : "Carica foto"}
              </Button>
              {form.photo_url && (
                <>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => upd("photo_url")("")}
                    data-testid="veh-photo-clear"
                  >
                    Rimuovi
                  </Button>
                  <img
                    src={form.photo_url}
                    alt="preview"
                    className="w-14 h-14 object-cover rounded border border-[color:var(--afb-cream)]"
                  />
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex items-center gap-3 p-3 border border-[color:var(--afb-cream)] rounded-md cursor-pointer hover:bg-afb-cream/40">
              <input
                type="checkbox"
                checked={!!form.active}
                onChange={(e) => upd("active")(e.target.checked)}
                className="w-4 h-4 accent-[color:var(--afb-gold)]"
                data-testid="veh-active"
              />
              <span className="text-sm text-afb-ink">Attivo (visibile sul sito)</span>
            </label>
            <Field
              label="Ordine"
              type="number"
              value={form.order}
              onChange={upd("order")}
              testId="veh-order"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-afb-muted">
              Date non disponibili
            </Label>
            <p className="text-xs text-afb-muted mt-1">
              Tocca le date in cui il veicolo è già impegnato. Usa "Blocca mese" per indisponibilità completa.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 mb-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => blockWholeMonth(today.getFullYear(), today.getMonth())}
                data-testid="veh-block-current-month"
              >
                Blocca tutto questo mese
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => clearMonth(today.getFullYear(), today.getMonth())}
                data-testid="veh-clear-current-month"
              >
                Libera questo mese
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => upd("blocked_dates")([])}
                data-testid="veh-clear-all"
              >
                Libera tutto
              </Button>
            </div>
            <div className="inline-block bg-white border border-[color:var(--afb-cream)] rounded-md p-3">
              <Calendar
                mode="multiple"
                selected={blockedAsDates}
                onSelect={onCalendarSelect}
                data-testid="veh-calendar"
                classNames={{
                  day: "h-9 w-9 p-0 font-semibold rounded-md bg-green-700 text-white hover:bg-green-600 transition-colors",
                  day_selected: "!bg-red-700 !text-white hover:!bg-red-800",
                  day_outside: "!bg-transparent !text-zinc-300 opacity-60",
                  day_today: "!ring-2 !ring-[color:var(--afb-gold)]",
                }}
              />
            </div>
            <AvailabilityLegend variant="admin" />
            {form.blocked_dates.length > 0 && (
              <div className="mt-2 text-xs text-afb-muted">
                {form.blocked_dates.length} giorni bloccati
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Annulla
            </Button>
            <Button
              type="submit"
              className="bg-afb-ink hover:bg-black text-white"
              data-testid="veh-save"
            >
              Salva
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}



function ServiceTypesEditor({ value, onChange }) {
  const items = Array.isArray(value) && value.length > 0
    ? value
    : [
        { value: "without_driver", label_it: "Senza autista", label_en: "Without driver" },
        { value: "with_driver", label_it: "Con autista", label_en: "With driver" },
        { value: "with_driver_helper", label_it: "Con autista + necroforo", label_en: "With driver + pallbearer" },
      ];

  const slugify = (s) =>
    (s || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 40) || `opt_${Date.now()}`;

  const update = (idx, field, val) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [field]: val } : it));
    onChange(next);
  };

  const add = () => {
    const base = "nuova_opzione";
    let v = base;
    let n = 1;
    const taken = new Set(items.map((it) => it.value));
    while (taken.has(v)) v = `${base}_${n++}`;
    onChange([...items, { value: v, label_it: "Nuova opzione", label_en: "New option" }]);
  };

  const remove = (idx) => {
    if (items.length <= 1) {
      toast.error("Devi mantenere almeno un tipo di servizio");
      return;
    }
    onChange(items.filter((_, i) => i !== idx));
  };

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    const next = items.slice();
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div data-testid="service-types-editor">
      <h3 className="serif text-base text-afb-ink mb-2">Tipi di servizio (radio pe pagina di prenotazione)</h3>
      <p className="text-xs text-afb-muted mb-4">
        Aggiungi, rinomina o rimuovi le opzioni che il cliente vedrà al momento della
        prenotazione. Almeno una opzione è obbligatoria.
      </p>

      <div className="space-y-2">
        {items.map((it, idx) => (
          <div
            key={`st-${idx}`}
            className="grid grid-cols-12 gap-2 items-center p-3 bg-white border border-[color:var(--afb-cream)] rounded-md"
            data-testid={`service-type-row-${idx}`}
          >
            <div className="col-span-12 sm:col-span-5">
              <Label className="text-[10px] uppercase tracking-widest text-afb-muted">
                Italiano
              </Label>
              <Input
                value={it.label_it || ""}
                onChange={(e) => {
                  const labelIt = e.target.value;
                  // Auto-sync the slug only if it was machine-generated (matches old auto slug)
                  const next = items.map((x, i) => {
                    if (i !== idx) return x;
                    const oldAuto = slugify(x.label_it || "");
                    const newSlug = (x.value === oldAuto) ? slugify(labelIt) : x.value;
                    return { ...x, label_it: labelIt, value: newSlug };
                  });
                  onChange(next);
                }}
                placeholder="Es. Con autista"
                data-testid={`st-label-it-${idx}`}
              />
            </div>
            <div className="col-span-12 sm:col-span-5">
              <Label className="text-[10px] uppercase tracking-widest text-afb-muted">
                English
              </Label>
              <Input
                value={it.label_en || ""}
                onChange={(e) => update(idx, "label_en", e.target.value)}
                placeholder="E.g. With driver"
                data-testid={`st-label-en-${idx}`}
              />
            </div>
            <div className="col-span-12 sm:col-span-2 flex sm:flex-col items-center justify-end gap-1 sm:pt-4">
              <button
                type="button"
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                className="w-7 h-7 rounded border border-[color:var(--afb-cream)] text-xs disabled:opacity-30 hover:bg-afb-cream/50"
                title="Sposta su"
                data-testid={`st-up-${idx}`}
              >↑</button>
              <button
                type="button"
                onClick={() => move(idx, 1)}
                disabled={idx === items.length - 1}
                className="w-7 h-7 rounded border border-[color:var(--afb-cream)] text-xs disabled:opacity-30 hover:bg-afb-cream/50"
                title="Sposta giù"
                data-testid={`st-down-${idx}`}
              >↓</button>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="w-7 h-7 rounded border border-red-200 text-red-600 hover:bg-red-50"
                title="Rimuovi"
                data-testid={`st-remove-${idx}`}
              >
                <Trash2 className="w-3.5 h-3.5 mx-auto" />
              </button>
            </div>
            <div className="col-span-12 text-[10px] text-afb-muted/70 -mt-1">
              <code>slug: {it.value}</code>
            </div>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        className="mt-3"
        data-testid="st-add"
      >
        <Plus className="w-4 h-4 mr-1" />
        Aggiungi opzione
      </Button>
      <p className="text-[11px] text-afb-muted mt-2">
        Ricorda di premere <strong>Salva</strong> in fondo alla scheda dopo le modifiche.
      </p>
    </div>
  );
}
